function parseSubscriptionData(item) {
  const dataEl = item.querySelector("[data-oo-upsell-subscription-data]");
  if (!dataEl) return null;

  try {
    return JSON.parse(dataEl.textContent);
  } catch (error) {
    console.error("oo-cart-upsell-subscription: invalid JSON", error);
    return null;
  }
}

function formatMoney(cents) {
  const amount = (cents || 0) / 100;
  if (window.Shopify?.formatMoney) {
    return window.Shopify.formatMoney(cents, window.theme?.moneyFormat);
  }
  return new Intl.NumberFormat(document.documentElement.lang || "nl-NL", {
    style: "currency",
    currency: window.Shopify?.currency?.active || "EUR",
  }).format(amount);
}

function findVariant(data, bags, flavor) {
  const bagCount = parseInt(bags, 10);
  const variants = (data.variants || []).filter((variant) => variant.bags === bagCount);

  if (!variants.length) return null;

  if (flavor) {
    const flavorMatch = variants.find(
      (variant) =>
        String(variant.flavor || "").toLowerCase() === String(flavor).toLowerCase(),
    );
    if (flavorMatch) return flavorMatch;
  }

  const preferredMatch = variants.find(
    (variant) =>
      String(variant.flavor || "").toLowerCase() ===
      String(data.preferredFlavor || "").toLowerCase(),
  );
  if (preferredMatch) return preferredMatch;

  return variants.find((variant) => variant.sellingPlans?.length) || variants[0];
}

function findPlan(variant, days) {
  const dayCount = parseInt(days, 10);
  return (variant?.sellingPlans || []).find((plan) => plan.days === dayCount) || null;
}

function getSubscriptionSelection(item) {
  const data = parseSubscriptionData(item);
  if (!data) return null;

  const bagsSelect = item.querySelector("[data-oo-upsell-bags]");
  const daysSelect = item.querySelector("[data-oo-upsell-days]");
  if (!bagsSelect || !daysSelect) return null;

  const variant = findVariant(data, bagsSelect.value, data.preferredFlavor);
  if (!variant) return null;

  const plan = findPlan(variant, daysSelect.value);
  if (!plan) return null;

  return { data, bagsSelect, daysSelect, variant, plan };
}

export function syncSubscriptionItem(item) {
  const selection = getSubscriptionSelection(item);
  if (!selection) return;

  const { variant, plan, bagsSelect, daysSelect } = selection;
  const variantInput = item.querySelector("[data-oo-upsell-variant-id]");
  const planInput = item.querySelector("[data-oo-upsell-selling-plan]");
  const priceEl = item.querySelector("[data-oo-upsell-price]");

  variantInput && (variantInput.value = String(variant.id));
  planInput && (planInput.value = String(plan.id));
  if (priceEl) priceEl.textContent = formatMoney(plan.price ?? variant.price);

  if (String(daysSelect.value) !== String(plan.days)) {
    daysSelect.value = String(plan.days);
  }
}

function setSubscriptionButtonLoading(button, isLoading) {
  if (!button) return;

  const loader = button.querySelector(".loading-overlay__spinner");
  button.toggleAttribute("aria-disabled", isLoading);
  button.classList.toggle("loading", isLoading);
  loader?.classList.toggle("hidden", !isLoading);
}

async function addSubscriptionToCart(item) {
  const selection = getSubscriptionSelection(item);
  if (!selection) return;

  const { variant, plan } = selection;
  const button = item.querySelector("[data-oo-upsell-subscription-submit]");
  const cart = document.querySelector("cart-drawer");

  if (button?.getAttribute("aria-disabled") === "true") return;

  syncSubscriptionItem(item);
  setSubscriptionButtonLoading(button, true);

  try {
    const config = fetchConfig("javascript");
    config.headers["X-Requested-With"] = "XMLHttpRequest";
    delete config.headers["Content-Type"];

    const formData = new FormData();
    formData.append("id", String(variant.id));
    formData.append("quantity", "1");
    formData.append("selling_plan", String(plan.id));

    if (cart?.getSectionsToRender) {
      formData.append(
        "sections",
        cart.getSectionsToRender().map((section) => section.id),
      );
      formData.append("sections_url", window.location.pathname);
    }

    config.body = formData;

    const response = await fetch(`${window.routes.cart_add_url}`, config).then((res) =>
      res.json(),
    );

    if (response.status) {
      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        publish(PUB_SUB_EVENTS.cartError, {
          source: "oo-cart-upsell-subscription",
          productVariantId: String(variant.id),
          errors: response.errors || response.description,
          message: response.message,
        });
      }
      return;
    }

    if (!cart) {
      window.location = window.routes.cart_url;
      return;
    }

    if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: "oo-cart-upsell-subscription",
        productVariantId: String(variant.id),
        cartData: response,
      });
    }

    if (cart.classList.contains("is-empty")) {
      cart.classList.remove("is-empty");
    }

    const isClosedCart = !document.body.classList.contains("page-overlay-cart-on");
    cart.renderContents(response, isClosedCart);
  } catch (error) {
    console.error("oo-cart-upsell-subscription: add to cart failed", error);
  } finally {
    setTimeout(() => setSubscriptionButtonLoading(button, false), 200);
  }
}

function bindSubscriptionItem(item) {
  const bagsSelect = item.querySelector("[data-oo-upsell-bags]");
  const daysSelect = item.querySelector("[data-oo-upsell-days]");
  const button = item.querySelector("[data-oo-upsell-subscription-submit]");

  if (item.dataset.ooUpsellSubscriptionBound === "true") return;
  item.dataset.ooUpsellSubscriptionBound = "true";

  bagsSelect?.addEventListener("change", () => syncSubscriptionItem(item));
  daysSelect?.addEventListener("change", () => syncSubscriptionItem(item));
  button?.addEventListener("click", (event) => {
    event.preventDefault();
    addSubscriptionToCart(item);
  });

  syncSubscriptionItem(item);
}

export function initCartUpsellSubscriptions(root) {
  if (!root) return;
  root.querySelectorAll("[data-oo-upsell-subscription]").forEach(bindSubscriptionItem);
}

if (!window.__ooCartUpsellSubscriptionClickBound) {
  window.__ooCartUpsellSubscriptionClickBound = true;

  document.addEventListener(
    "click",
    (event) => {
      const button = event.target.closest("[data-oo-upsell-subscription-submit]");
      if (!button || button.getAttribute("aria-disabled") === "true") return;

      const item = button.closest("[data-oo-upsell-subscription]");
      if (!item || item.dataset.ooUpsellSubscriptionBound === "true") return;

      event.preventDefault();
      addSubscriptionToCart(item);
    },
    true,
  );
}
